import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { TranslatePipe } from '@spartacus/core';
import {
  CurrentProductService,
  OutletDirective,
  ProductDetailOutlets,
  ProductSummaryComponent,
} from '@spartacus/storefront';
import { Observable, Subscription, take } from 'rxjs';
import { distinct, distinctUntilKeyChanged, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { ShimanoProduct } from 'src/app/model/product.model';
import { BaseSiteConfigService } from 'src/app/service/base-site/base-site-config.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';

@Component({
  selector: 'app-shimano-product-summary',
  templateUrl: './shimano-product-summary.component.html',
  styleUrls: ['./shimano-product-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  imports: [OutletDirective, AsyncPipe, TranslatePipe],
})
export class ShimanoProductSummaryComponent extends ProductSummaryComponent implements OnInit, OnDestroy {
  outlets = ProductDetailOutlets;

  product$: Observable<ShimanoProduct> = this.currentProductService.getProduct() as Observable<ShimanoProduct>;
  isFloatSubscription$: Subscription;
  pdpDealerPrice: number;
  pdpDealerPriceSubscription$: Subscription;
  productSubscription$: Subscription;
  gtmProductDetailsSubscription$: Subscription;
  businessType: string;
  janCodeSubscription: Subscription;
  janCodeEnabled: boolean;
  discontinuedProductSubscription$: Subscription;
  isDiscontinued: boolean;
  isDiscountinuedEnabled: boolean;

  float = document.querySelector('app-shimano-product-summary');
  categories: any = {};
  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  constructor(
    protected currentProductService: CurrentProductService,
    private componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService,
    private currentBusinesstype: CurrentBusinessType,
    private baseSiteConfigService: BaseSiteConfigService,
    private storageService: StorageService,
    private countryService?: ShimanoCountryService,
    private languageService?: ShimanoLanguageService
  ) {
    super(currentProductService);
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  ngOnInit(): void {
    this.isFloatSubscription$ = this.componentService.isFloat$.subscribe((isFloat) => {
      isFloat ? this.float?.classList.add('float') : this.float?.classList.remove('float');
      this.cdr.detectChanges();
    });

    this.isDiscountinuedStatusEnabled();
    this.gtmProductDetailsSubscription$ = this.componentService.pdpDealerPrice$
      .pipe(
        withLatestFrom(this.product$.pipe(distinct())),
        distinctUntilKeyChanged(0),
        distinctUntilKeyChanged(1, (a, b) => a.code === b.code),
        tap(([pdpDealerPrice, product]) => {
          if (product && pdpDealerPrice) {
            this.gtmProductDetails(product, pdpDealerPrice);
          }
        })
      )
      .subscribe();

    this.discontinuedProductSubscription$ = this.product$
      .pipe(
        filter(Boolean),
        map((product: any) => {
          if (product && product.stock.stockLevelStatus === 'discontinued') {
            this.isDiscontinued = true;
            this.componentService.setIsDiscontinued(true);
          } else {
            this.isDiscontinued = false;
            this.componentService.setIsDiscontinued(false);
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.businessType = this.currentBusinesstype.getBusinessType();
    this.isJanCodeEnabled();
  }

  gtmProductDetails(product: any, dealerPrice: number) {
    this.categories = {};
    product.categories.forEach((cat: any, i: any) => {
      const indexCount = i + 1;
      const label = `item_category${indexCount == 1 ? '' : indexCount}`;
      this.categories[label] = cat.name;
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_DETAILS,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        items: [
          {
            item_id: product.code,
            item_name: product.name,
            affiliation: this.affiliation,
            item_brand: product?.brand?.name,
            ...this.categories,
            srp_price: product.srpPrice.value,
            base_price: product.basePrice.value,
            quantity: 1,
          },
        ],
      },
    };

    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  isJanCodeEnabled = () => {
    this.janCodeSubscription = this.getJanCodeConfig$().subscribe((janCode) => {
      this.janCodeEnabled = janCode === 'true';
      this.cdr.detectChanges();
    });
  };

  isDiscountinuedStatusEnabled(): void {
    this.getDiscountedStatusConfig$()
      .pipe(take(1))
      .subscribe((enabled) => {
        this.isDiscountinuedEnabled = enabled;
      });
  }

  getDiscountedStatusConfig$(): Observable<boolean> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.discontinued.status.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.discontinued.status.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  getJanCodeConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.ean.display.cart.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.ean.display.cart.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  ngOnDestroy() {
    if (this.isFloatSubscription$) {
      this.isFloatSubscription$.unsubscribe();
    }
    if (this.gtmProductDetailsSubscription$) {
      this.gtmProductDetailsSubscription$.unsubscribe();
    }
    if (this.janCodeSubscription) {
      this.janCodeSubscription.unsubscribe();
    }
    if (this.discontinuedProductSubscription$) {
      this.discontinuedProductSubscription$.unsubscribe();
    }
    this.componentService.setIsDiscontinued(false);
  }
}
